import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import AuthProvider, { AuthModule } from "./AuthProvider";


export interface AuthState {
  isAuthenticated: boolean;
  state: AuthModule.LoginResponse | undefined;
  authRef: React.MutableRefObject<AuthModule.LoginResponse>;
  handleUpdateAuthState: (state: AuthModule.LoginResponse) => void;
  handleUpdateRefreshTokens: (state: AuthModule.LoginResponse) => void;
  handleIntentionalLogout: () => void;
}

interface AuthStateContext
  extends Omit<
    AuthModule.LoginResponse,
    "authRef" | "handleUpdateAuthState" | "handleUpdateRefreshTokens"
  > {
  isAuthenticated: boolean;
  isLoginPending: boolean;
}

export const AuthStateContext = React.createContext<AuthStateContext>(
  undefined!
);

export function useAuthState() {
  const context = React.useContext(AuthStateContext);

  if (context == null) {
    throw new Error(
      `Invocations to ${useAuthState.name} should be within a node that is a child of ${AuthProvider.name}`
    );
  }

  return context;
}

export const useAuthenticationState = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const authRef = React.useRef<AuthModule.LoginResponse>(undefined!);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const intentionalLogoutRef = React.useRef(false);

  const handleUpdateAuthState = React.useCallback(
    (state: AuthModule.LoginResponse) => {
      const key = AuthModule.AUTH_STATE_STORAGE_KEY;
      try {
        const isValid = AuthModule.loginResponseSchema.validateSync(state, {
          stripUnknown: true,
        });

        authRef.current = state;
        setIsAuthenticated(true);
        localStorage.setItem(key, JSON.stringify(state));
      } catch (error) {
        // TODO log validation to api
        // if (error instanceof yup.ValidationError) {
        //   console.error(error.errors);
        // }
        setIsAuthenticated(false);
        localStorage.removeItem(key);
      }
    },
    []
  );

  const handleUpdateRefreshTokens = React.useCallback(
    (state: AuthModule.LoginResponse) => {
      const key = AuthModule.AUTH_STATE_STORAGE_KEY;
      authRef.current = state;
      localStorage.setItem(key, JSON.stringify(state));
    },
    []
  );

  const handleIntentionalLogout = React.useCallback(() => {
    intentionalLogoutRef.current = true;
    handleUpdateAuthState(undefined!);
  }, [handleUpdateAuthState]);

  useIniliatizeAuthState(handleUpdateAuthState, navigate, pathname, intentionalLogoutRef);

  return {
    isAuthenticated,
    authRef,
    state: authRef.current,
    handleUpdateAuthState,
    handleUpdateRefreshTokens,
    handleIntentionalLogout,
  };
};

const useIniliatizeAuthState = (
  handleUpdateAuthState: (state: AuthModule.LoginResponse) => void,
  navigate: (path: string) => void,
  pathname: string,
  intentionalLogoutRef: React.MutableRefObject<boolean>
) => {
  React.useEffect(() => {
    const itemKey = AuthModule.AUTH_STATE_STORAGE_KEY;

    const initializeAuthStateOrLogout = () => {
      const authState = localStorage.getItem(itemKey);
      
      if (authState && authState !== "undefined" && authState !== "null") {
        try {
          const state: AuthModule.LoginResponse = {
            ...JSON.parse(authState),
          };
          
          handleUpdateAuthState(state);
          return;
        } catch (parseError) {
          console.error('❌ Failed to parse auth state from localStorage:', parseError);
          // Clear corrupted auth state
          localStorage.removeItem(itemKey);
        }
      }

      handleUpdateAuthState(undefined!);
      // Only redirect to login if the user is not already on an auth page
      if (pathname !== "/" && pathname !== "/login" && pathname !== "/register" && !pathname.includes("/forgot-password")) {
        navigate("/");
      }

      // Only show session expired toast if this wasn't an intentional logout
      if (!intentionalLogoutRef.current) {
        toast.error("Please login again, your session has expired", {
          toastId: "session-expired",
          position: "top-right",
        });
      } else {
        // Reset the flag after handling intentional logout
        intentionalLogoutRef.current = false;
      }
    };

    initializeAuthStateOrLogout();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key !== itemKey || typeof window === "undefined") return;
      initializeAuthStateOrLogout();
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleUpdateAuthState, navigate, pathname]);
};
